<template>
	<div class="ark-file-upload">
		<FileUpload.Root
			:key="uploadKey"
			:accept="acceptRecord"
			:disabled="disabled || isUploading"
			:max-files="100"
			allow-drop
			@file-change="handleFileChange"
		>
			<FileUpload.Dropzone class="dropzone" :class="{ 'is-success': isSuccess }">
				<span v-if="isUploading" class="label">{{ uploadingLabel }}</span>
				<span v-else-if="isSuccess" class="label success">{{ successLabel }}</span>
				<span v-else class="label">{{ idleLabel }}</span>
			</FileUpload.Dropzone>
			<FileUpload.HiddenInput />
		</FileUpload.Root>
		<p v-if="uploadError" class="error">{{ uploadError }}</p>
	</div>
</template>

<script setup lang="ts">
import { FileUpload } from "@ark-ui/vue"
import { ref, computed } from "vue"

const props = defineProps<{
	onUpload: (file: File) => Promise<void>
	accept?: string[]
	disabled?: boolean
	idleLabel?: string
	uploadingLabel?: string
	successLabel?: string
}>()

const isUploading = ref(false)
const isSuccess = ref(false)
const uploadError = ref<string | null>(null)
// incremented after each upload to remount and clear internal file queue
const uploadKey = ref(0)

const idleLabel = computed(() => props.idleLabel ?? "Drop a file here or click to upload")
const uploadingLabel = computed(() => props.uploadingLabel ?? "Uploading...")
const successLabel = computed(() => props.successLabel ?? "Document uploaded successfully")

const acceptRecord = computed(() => {
	if (!props.accept?.length) return undefined
	return Object.fromEntries(props.accept.map(mime => [mime, []]))
})

async function handleFileChange({ acceptedFiles }: { acceptedFiles: File[] }) {
	if (!acceptedFiles?.length) return
	isUploading.value = true
	isSuccess.value = false
	uploadError.value = null
	try {
		for (const file of acceptedFiles) await props.onUpload(file)
		isSuccess.value = true
		setTimeout(() => (isSuccess.value = false), 3000)
	} catch (err) {
		uploadError.value = err instanceof Error ? err.message : "Upload failed"
	} finally {
		isUploading.value = false
		uploadKey.value++
	}
}
</script>

<style scoped>
.ark-file-upload {
	display: block;

	.dropzone {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: var(--spacing-md);
		border: var(--border-width-thin) dashed var(--neutral-400);
		border-radius: var(--border-radius-sm);
		cursor: pointer;
		transition:
			border-color 0.2s,
			background-color 0.2s;

		&:hover,
		&[data-dragging] {
			border-color: var(--primary-green);
			background-color: var(--accent-highlight-light);
		}

		&[data-disabled] {
			opacity: 0.5;
			cursor: not-allowed;
		}

		&.is-success {
			border-color: var(--primary-green);
		}

		.label {
			font-size: var(--font-size-small);
			color: var(--neutral-600);
			text-align: center;

			&.success {
				color: var(--primary-green);
			}
		}
	}

	.error {
		margin-top: var(--spacing-sm);
		font-size: var(--font-size-small);
		color: var(--accent-warning);
	}
}
</style>
